import React, { useState, useContext, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../auth/AuthContext';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { FiUser, FiLock, FiMail, FiShield, FiCheck, FiAlertCircle, FiFolderPlus } from 'react-icons/fi';
import { getMyProjects } from '../api/projectApi';
import { COLORS as C, RADIUS } from '../constants/ui';
import MenuOrderPreference from '../components/MenuOrderPreference';

const AccountSettings = () => {
  const { user: authUser, updateUser } = useContext(AuthContext);
  const currentRole = (authUser?.role || '').toLowerCase();
  const canEditEmail = ['admin', 'superadmin', 'manager'].includes(currentRole);
  const queryClient = useQueryClient();
  
  const [profileForm, setProfileForm] = useState({ full_name: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');

  const { data: user, isLoading } = useQuery({
    queryKey: ['current-user'],
    queryFn: () => api.get('/users/me').then(r => r.data),
  });

  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['my-projects'],
    queryFn: () => getMyProjects().then(r => r.data),
  });

  // Update form when user data loads
  useEffect(() => {
    if (user) {
      setProfileForm({ full_name: user.full_name || '', email: user.email || '' });
    }
  }, [user]);

  const updateProfileMutation = useMutation({
    mutationFn: (data) => api.put('/users/me', data).then(r => r.data),
    onSuccess: (data) => {
      toast.success('Profile updated successfully');
      queryClient.invalidateQueries(['current-user']);
      // Sync updated user data to AuthContext so login email is updated
      if (updateUser && data) {
        updateUser(data);
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    }
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data) => api.put('/users/me/password', data).then(r => r.data),
    onSuccess: () => {
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordError('');
    },
    onError: (err) => {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    }
  });

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    const payload = canEditEmail
      ? profileForm
      : { full_name: profileForm.full_name };
    updateProfileMutation.mutate(payload);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword
    });
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: C.inkSoft }}>
        Loading...
      </div>
    );
  }

  const roleBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: '0.8rem',
    fontWeight: 700,
    background: C.accentSoft,
    color: C.accentInk
  };

  return (
    <div style={{ padding: '2rem', background: C.sunken, minHeight: '100vh' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: C.ink, marginBottom: '2rem' }}>
        Account Settings
      </h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))', gap: '1.5rem', maxWidth: 900 }}>
        
        {/* Profile Section */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <FiUser size={20} />
            <span>Profile Information</span>
          </div>
          
          <form onSubmit={handleProfileSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Email</label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FiMail size={16} style={{ marginRight: 8, color: C.inkSoft }} />
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => canEditEmail && setProfileForm({ ...profileForm, email: e.target.value })}
                  style={{ ...inputStyle, flex: 1, background: canEditEmail ? '' : C.sunken, color: canEditEmail ? '' : C.inkSoft, cursor: canEditEmail ? '' : 'not-allowed' }}
                  placeholder="your.email@example.com"
                  readOnly={!canEditEmail}
                />
              </div>
              <p style={{ fontSize: '0.75rem', color: C.inkFaint, marginTop: 4 }}>
                {canEditEmail ? 'Email must be unique and valid' : 'Contact admin or manager to change your email'}
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Role</label>
              <div style={{ marginTop: 4 }}>
                <span style={roleBadgeStyle}>
                  <FiShield size={14} />
                  {(user?.role || authUser?.role || 'user')?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <p style={{ fontSize: '0.75rem', color: C.inkFaint, marginTop: 4 }}>
                Contact admin to change role
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Full Name</label>
              <input
                type="text"
                value={profileForm.full_name}
                onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                style={inputStyle}
                placeholder="Your full name"
              />
            </div>

            <button
              type="submit"
              disabled={updateProfileMutation.isPending}
              style={buttonPrimaryStyle}
            >
              <FiCheck size={16} />
              {updateProfileMutation.isPending ? 'Saving...' : 'Save Profile'}
            </button>
          </form>

          {/* Projects Section */}
          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: `1px solid ${C.line}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
              <FiFolderPlus size={18} style={{ color: C.accentInk }} />
              <label style={{ ...labelStyle, marginBottom: 0 }}>Assigned Projects</label>
            </div>
            {projectsLoading ? (
              <div style={{ fontSize: '0.85rem', color: C.inkFaint }}>Loading projects...</div>
            ) : projects.length === 0 ? (
              <div style={{ 
                padding: '1rem', 
                background: C.warnSoft, 
                borderRadius: 8, 
                fontSize: '0.85rem', 
                color: '#92400e',
                border: '1px solid #fde68a'
              }}>
                You are not assigned to any projects yet. Contact your manager to get assigned.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {projects.map(project => (
                  <div key={project.projectid} style={{
                    padding: '0.75rem 1rem',
                    background: project.status === 'Active' ? '#f0fdf4' : C.sunken,
                    border: `1px solid ${project.status === 'Active' ? '#bbf7d0' : C.line}`,
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: C.ink }}>
                        {project.projectname}
                      </div>
                      {project.role_in_project && (
                        <div style={{ fontSize: '0.75rem', color: C.inkSoft, marginTop: 2 }}>
                          Role: {project.role_in_project}
                        </div>
                      )}
                    </div>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: 12,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      background: project.status === 'Active' ? C.doneSoft : C.sunken,
                      color: project.status === 'Active' ? '#15803d' : C.inkSoft
                    }}>
                      {project.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Password Section */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <FiLock size={20} />
            <span>Change Password</span>
          </div>

          {passwordError && (
            <div style={errorAlertStyle}>
              <FiAlertCircle size={16} />
              {passwordError}
            </div>
          )}

          <form onSubmit={handlePasswordSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Current Password</label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => { setPasswordForm({ ...passwordForm, currentPassword: e.target.value }); setPasswordError(''); }}
                style={inputStyle}
                placeholder="Enter current password"
                required
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>New Password</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => { setPasswordForm({ ...passwordForm, newPassword: e.target.value }); setPasswordError(''); }}
                style={inputStyle}
                placeholder="Min 6 characters"
                required
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Confirm New Password</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => { setPasswordForm({ ...passwordForm, confirmPassword: e.target.value }); setPasswordError(''); }}
                style={inputStyle}
                placeholder="Repeat new password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={changePasswordMutation.isPending}
              style={buttonPrimaryStyle}
            >
              <FiLock size={16} />
              {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
            </button>
          </form>

          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f0f9ff', borderRadius: 8, fontSize: '0.8rem', color: '#0369a1' }}>
            <strong>Forgot your password?</strong>
            <p style={{ margin: '4px 0 0' }}>
              Contact your Manager or Admin. They can generate a password reset link for you to set a new password.
            </p>
          </div>
        </div>

        {/* Personal sidebar arrangement — reorder only; visibility and names
            stay a department-level decision. */}
        <MenuOrderPreference />

      </div>
    </div>
  );
};

// Styles
const cardStyle = {
  background: '#fff',
  borderRadius: 16,
  border: `1px solid ${C.line}`,
  padding: '1.5rem',
};

const cardHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontSize: '1.1rem',
  fontWeight: 700,
  color: C.accentInk,
  marginBottom: '1.5rem',
  paddingBottom: '1rem',
  borderBottom: `1px solid ${C.line}`
};

const labelStyle = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: C.ink,
  marginBottom: 6
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem 1rem',
  fontSize: '0.9rem',
  border: `1px solid ${C.upcoming}`,
  borderRadius: 8,
  outline: 'none',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center'
};

const buttonPrimaryStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '0.75rem 1.5rem',
  background: 'linear-gradient(135deg, #1272B3 0%, #1272B3 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontWeight: 700,
  fontSize: '0.9rem',
  cursor: 'pointer',
  width: '100%'
};

const errorAlertStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0.75rem 1rem',
  background: C.dangerSoft,
  border: '1px solid #fecaca',
  borderRadius: 8,
  color: C.danger,
  fontSize: '0.85rem',
  fontWeight: 500,
  marginBottom: '1rem'
};

export default AccountSettings;
